import { 
  Typography, 
  Box, 
  Container, 
  Grid, 
  Card, 
  CardContent, 
  Chip, 
  Button
} from '@mui/material';
import CodeIcon from '@mui/icons-material/Code';
import BrushIcon from '@mui/icons-material/Brush';
import SpeedIcon from '@mui/icons-material/Speed';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SEO from 'features/shared/components/common/SEO';
import { motion } from 'framer-motion';
import PortfolioGallery from '../../../components/PortfolioGallery';

export default function CreacionPaginasWeb() {

  return (
    <>
      <SEO 
        title="Creación de Páginas Web | Rolsys Solutions"
        description="Diseño y desarrollo de sitios web modernos, rápidos y optimizados para SEO. Hacemos que tu negocio destaque en línea."
      />

      {/* Hero Section */}
      <Box 
        sx={{ 
          pt: { xs: 8, md: 12 }, 
          pb: { xs: 6, md: 10 },
          background: 'linear-gradient(135deg, rgba(25,118,210,0.05) 0%, rgba(25,118,210,0) 100%)',
          borderRadius: { xs: 0, md: 4 },
          mb: 8
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
              <Typography variant="h2" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Creación de <span style={{ color: '#1976d2' }}>Páginas Web</span>
              </Typography>
              <Chip label="Servicio Global" color="primary" variant="outlined" />
            </Box>
            
            <Typography variant="h6" sx={{ color: 'text.secondary', maxWidth: '800px', mb: 4, lineHeight: 1.6 }}>
              Desarrollamos experiencias digitales únicas. Sitios web ultrarrápidos, optimizados para buscadores (SEO) y diseñados con las últimas tendencias de UI/UX para convertir visitantes en clientes.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Features Section */}
        <Grid container spacing={4} sx={{ mb: 10 }}>
          {[
            { icon: <BrushIcon fontSize="large" color="primary"/>, title: 'Diseño UI/UX Premium', desc: 'Interfaces limpias, modernas y adaptadas perfectamente a dispositivos móviles.' },
            { icon: <SpeedIcon fontSize="large" color="primary"/>, title: 'Rendimiento Extremo', desc: 'Tiempos de carga reducidos al máximo cumpliendo los estándares de Core Web Vitals.' },
            { icon: <CodeIcon fontSize="large" color="primary"/>, title: 'Desarrollo a la Medida', desc: 'Código limpio, escalable y arquitecturas modernas basadas en React.' },
          ].map((feature, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <Card elevation={0} sx={{ height: '100%', border: '1px solid #eaeaea', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 30px rgba(0,0,0,0.05)' } }}>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ mb: 2 }}>{feature.icon}</Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{feature.title}</Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>{feature.desc}</Typography>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>

        <PortfolioGallery />

        {/* Hostinger Promotion Section */}
        <Box sx={{ mb: 10, mt: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <Card elevation={0} sx={{ 
              border: '1px solid #eaeaea', 
              borderRadius: 4, 
              overflow: 'hidden',
              boxShadow: '0 10px 40px rgba(0,0,0,0.05)'
            }}>
              <Grid container>
                <Grid item xs={12} md={7} sx={{ p: { xs: 4, md: 6 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#673DE6', textTransform: 'uppercase', letterSpacing: 1, mb: 2 }}>
                    PARTNER OFICIAL DE HOSTINGER
                  </Typography>
                  <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
                    Seguro, rápido y confiable
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4, fontSize: '1.1rem', lineHeight: 1.7 }}>
                    El hosting web es ideal para sitios personales, empresariales y de ecommerce. Se incluyen un dominio gratuito, email profesional y migración de sitios web. Disfruta de respaldos automáticos semanales para una fácil recuperación de datos.
                  </Typography>
                  
                  <Box sx={{ mb: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <CheckCircleOutlineIcon sx={{ color: '#673DE6' }} />
                      <Typography variant="body1">Obtén hasta un <strong>20% de descuento</strong> en tu plan</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <SpeedIcon sx={{ color: '#673DE6' }} />
                      <Typography variant="body1">Incluye el panel de control <strong>hPanel</strong></Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <CardGiftcardIcon sx={{ color: '#673DE6' }} />
                      <Typography variant="body1">Certificado SSL ilimitado y gratis</Typography>
                    </Box>
                  </Box>

                  <Button 
                    variant="contained" 
                    size="large"
                    href="https://www.hostinger.com/mx?REFERRALCODE=RXREDOARD5E8"
                    target="_blank"
                    sx={{ 
                      backgroundColor: '#673DE6', 
                      '&:hover': { backgroundColor: '#5B36CC' },
                      borderRadius: 2, 
                      textTransform: 'none', 
                      px: 4,
                      py: 1.5,
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      mb: 2
                    }}
                  >
                    Me interesa
                  </Button>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontSize: '0.75rem', mt: 1 }}>
                    * Hostinger y su logotipo son marcas registradas de Hostinger International, Ltd.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5} sx={{ backgroundColor: '#f3e5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 4, md: 0 } }}>
                  <Box sx={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <img src="/img/hosting_illustration.png" alt="Hostinger Web Hosting" style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain' }} />
                  </Box>
                </Grid>
              </Grid>
            </Card>
          </motion.div>
        </Box>

      </Container>

    </>
  );
}
